// src/components/agent/session/partModel.js
//
// The unified part model (frontend spec §5): both the Firestore listener and
// the SSE reducer emit these ViewPart/ViewMessage shapes, and every message
// renders through one component tree regardless of source.
//
// ViewPart kinds:
//   { kind: 'text',        text }
//   { kind: 'tool_call',   toolUseId, name, statusMessage, isError, completed, executing }
//   { kind: 'charts',      toolUseId, gcsPath }
//   { kind: 'table',       toolUseId, gcsPath }
//   { kind: 'file',        toolUseId, filename, description, downloadUrl | gcsPath, sandboxPath,
//                          pushToCanvas }
//   { kind: 'notice',      noticeKind, text }
//   { kind: 'approval',    …the hydrated durable approval model… }
//                          // built ONLY by session/approvalHydration.js from the
//                          // persisted record (live event or Firestore snapshot);
//                          // the merge layer decorates it with
//                          // { decisionStatus, onDecide }
//   user-side:
//   { kind: 'user_text',   text }
//   { kind: 'upload',      filename, fileKind }
//   { kind: 'images',      filename, fileKind, image_assets, total_page_count }
//   { kind: 'canvas_edit', source_filename, source_tool_use_id, edit_count,
//                          gcsPath, sandboxPath }
//
// ViewMessage:
//   { id, role, state, parts, clientMessageId, timestampMs, live,
//     compaction? }    // system compaction divider rows only

// Tolerant timestamp → millis. The v2 backend is Python, so Firestore
// Timestamp objects are the norm; ISO strings and raw numbers are tolerated
// defensively (in the spirit of ChatHistory's parseCreatedAt).
export const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value === 'object') {
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (typeof value.seconds === 'number') {
      return (value.seconds * 1000) + Math.floor((value.nanoseconds || 0) / 1e6);
    }
    if (value instanceof Date) return value.getTime();
  }
  if (typeof value === 'number') return value;
  const parsed = new Date(String(value));
  return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

export const inferUploadKind = (filename = '') => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['xlsx', 'xls', 'csv', 'json', 'docx'].includes(ext)) return 'structured';
  if (['pdf'].includes(ext)) return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  return 'file';
};


// Pure mapping from a v2 doc part to a ViewPart (spec §4.4 table).
// Unknown part types map to nothing — the doc-side mirror of the
// unknown-SSE-event rule, so a future backend part type degrades silently.
export const partFromDoc = (part, role) => {
  if (!part || typeof part !== 'object') return null;
  // Backend-authored parts that exist for the model's memory, not for humans
  // (e.g. the durable interrupted-turn notes), carry display_to_user: false.
  // Absent means display — every part written before the flag existed shows.
  if (part.display_to_user === false) return null;
  switch (part.type) {
    case 'text':
      return role === 'user'
        ? { kind: 'user_text', text: part.text || '' }
        : { kind: 'text', text: part.text || '' };
    case 'tool_call': {
      // A background launch answers with the job envelope; the chip renders
      // it as "Background job submitted" rather than the tool's normal
      // completed status.
      let backgroundJob = false;
      if (!part.is_error && typeof part.result_json === 'string') {
        try {
          const result = JSON.parse(part.result_json);
          backgroundJob = !!result
            && typeof result === 'object'
            && typeof result.job_id === 'string'
            && result.status === 'running';
        } catch {
          // Not JSON — not an envelope.
        }
      }
      return {
        kind: 'tool_call',
        toolUseId: part.tool_use_id,
        name: part.name,
        statusMessage: part.status_message || null,
        isError: !!part.is_error,
        // Refused by policy or by the person: an error for the model, but
        // shown as a refusal rather than a failure.
        denied: !!part.denied,
        // Completion = result present (inline or spilled) or errored.
        completed: part.result_json != null || part.result_gcs_path != null || !!part.is_error,
        executing: false,
        backgroundJob,
      };
    }
    case 'charts':
      return { kind: 'charts', toolUseId: part.tool_use_id, gcsPath: part.charts_gcs_path };
    case 'table':
      return { kind: 'table', toolUseId: part.tool_use_id, gcsPath: part.table_gcs_path };
    case 'file':
      return {
        kind: 'file',
        toolUseId: part.tool_use_id,
        filename: part.filename,
        description: part.description,
        gcsPath: part.file_gcs_path,
        downloadUrl: null,
        sandboxPath: part.sandbox_path || null,
        // The same flag the live canvas_file event is emitted from, so a push
        // missed while the stream was down is still recoverable from the ledger.
        pushToCanvas: !!part.push_to_canvas,
      };
    case 'notice':
      return { kind: 'notice', noticeKind: part.kind, text: part.text || '' };
    case 'file_upload':
      return {
        kind: 'upload',
        filename: part.filename,
        fileKind: part.kind,
        gcsPath: part.gcs_path || null,
        sandboxPath: part.sandbox_path || null,
      };
    case 'images':
      // The part carries the original file's own path; its assets are the
      // previews (the image at the size the model read it, or rendered pages).
      return {
        kind: 'images',
        filename: part.filename,
        fileKind: part.kind,
        image_assets: part.image_assets || [],
        total_page_count: part.total_page_count || 0,
        gcsPath: part.gcs_path || null,
        sandboxPath: part.sandbox_path || null,
      };
    case 'canvas_edit':
      return {
        kind: 'canvas_edit',
        source_filename: part.source_filename,
        source_tool_use_id: part.source_tool_use_id || '',
        edit_count: part.edit_count ?? (Array.isArray(part.edits) ? part.edits.length : 0),
        // The edited workbook the user sent, and the sandbox file it replaced.
        // Together they make this part a saved version of that working file.
        gcsPath: part.gcs_path || null,
        sandboxPath: part.sandbox_path || null,
      };
    default:
      return null;
  }
};

// Merge adjacent text parts (plain concatenation — byte-identical with the
// reducer's delta accumulation), making the doc view match the reducer's
// single-trailing-text rule by construction (§5).
const mergeAdjacentText = (parts) => {
  const merged = [];
  for (const part of parts) {
    const prev = merged.length ? merged[merged.length - 1] : null;
    if (prev && (part.kind === 'text' || part.kind === 'user_text') && prev.kind === part.kind) {
      merged[merged.length - 1] = { ...prev, text: prev.text + part.text };
    } else {
      merged.push(part);
    }
  }
  return merged;
};

// The state-aware doc mapping (spec §6.2). Returns a ViewMessage, or null for
// docs the render list hides (aborted, unrenderable roles). Compaction records
// map to a "conversation summarised" divider row.
const ledgerOrdinal = (docData) => (
  typeof docData._ledger_ordinal === 'number' ? docData._ledger_ordinal : null
);

export const messageFromDoc = (docData, id) => {
  if (!docData) return null;
  if (docData.content_revision != null) {
    throw new Error('Message content fragments must be hydrated before rendering');
  }

  if (docData.role !== 'user' && docData.role !== 'assistant') {
    if (docData.role === 'system' && docData.type === 'compaction') {
      return {
        id,
        role: 'system',
        state: 'complete',
        parts: [],
        compaction: true,
        clientMessageId: null,
        timestampMs: toMillis(docData.timestamp),
        ordinal: ledgerOrdinal(docData),
        live: null,
      };
    }
    return null; // never an empty bubble
  }

  const state = docData.state || 'complete';
  if (state === 'aborted') return null; // kept for forensics; the UI hides them

  const parts = mergeAdjacentText(
    (docData.content || [])
      .map((part) => partFromDoc(part, docData.role))
      .filter(Boolean)
  );

  // An awaiting_approval doc deliberately synthesises NOTHING here: the parked
  // cycle's approval is rebuilt from its durable `pending_approvals` record by
  // the shared hydrator, which is the only source of approval detail.

  // The runner records a user-requested Stop as a text note kept for the
  // model's memory but hidden from the transcript — the only part it writes
  // with display_to_user: false. It is surfaced as a durable notice in the
  // thread's own words, so a stopped turn does not read as complete after a
  // reload (M157).
  const stoppedByUser = docData.role === 'assistant' && (docData.content || []).some(
    (part) => part?.type === 'text' && part.display_to_user === false,
  );

  return {
    id,
    role: docData.role,
    state,
    parts,
    stoppedByUser,
    clientMessageId: docData.client_message_id || null,
    timestampMs: toMillis(docData.timestamp),
    ordinal: ledgerOrdinal(docData),
    live: null,
  };
};

// The ledger's own order. Every message and compaction record carries the append
// ordinal the store allocated; a compaction record takes the ordinal of the user
// message it was inserted ahead of (which moves up by one), so its timestamp — later
// than that message's — must not decide where the divider renders.
export const compareLedgerOrder = (a, b) => (
  typeof a.ordinal === 'number' && typeof b.ordinal === 'number' ? a.ordinal - b.ordinal : 0
);

// The workbooks the agent's most recent turn pushed to the canvas pane, in the
// order it pushed them. The canvas_file SSE event announces the same pushes as
// they happen; this reads them back out of the ledger, which is the only source
// a page that lost the stream has. Only the last turn counts, so an earlier
// turn's workbook is never reopened once a later turn has moved on, and the
// pane is whatever the agent has most recently produced.
export const lastTurnCanvasPushes = (messages = []) => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.role !== 'assistant') continue;
    return (message.parts || []).filter((part) => part.kind === 'file' && part.pushToCanvas);
  }
  return [];
};

// Every saved version of one working file, oldest first. A working file is a
// file in the agent's sandbox, named by its sandbox path, and it has one saved
// version for every time it was accepted into the chat: each time the agent
// returned it (an assistant `file` part) and each time a user sent canvas edits
// to it (a user `canvas_edit` part, whose upload overwrote the sandbox file).
// `messages` must be in ledger order. Opening an older version and then sending
// edits would silently undo the versions after it (bug 13.1).
//
// `toolUseId` is what a canvas edit of a version must name as its source: the
// backend resolves the target sandbox file from an agent file part.
export const workingFileVersions = (messages = [], sandboxPath) => {
  if (!sandboxPath) return [];
  const versions = [];
  messages.forEach((message) => {
    (message.parts || []).forEach((part) => {
      const returnedByAgent = message.role === 'assistant' && part.kind === 'file';
      const editedByUser = message.role === 'user' && part.kind === 'canvas_edit';
      if (!returnedByAgent && !editedByUser) return;
      if (part.sandboxPath !== sandboxPath || !part.gcsPath) return;
      versions.push({
        gcsPath: part.gcsPath,
        toolUseId: returnedByAgent ? part.toolUseId : part.source_tool_use_id,
        filename: returnedByAgent ? part.filename : part.source_filename,
        editedByUser,
        clientMessageId: message.clientMessageId || null,
      });
    });
  });
  return versions;
};

// The newest saved version of one working file, or null when there is none.
export const newestWorkingFileVersion = (messages = [], sandboxPath) => {
  const versions = workingFileVersions(messages, sandboxPath);
  return versions.length > 0 ? versions[versions.length - 1] : null;
};

// The agent's file part a canvas workbook was opened from, found by the tool
// call that produced it. A card that is still streaming carries neither a
// storage path nor a sandbox path, only this id; the ledger has both once the
// part reaches it. Null until then.
export const agentFilePartForToolUse = (messages = [], toolUseId) => {
  if (!toolUseId) return null;
  const fileParts = messages
    .filter((message) => message.role === 'assistant')
    .flatMap((message) => message.parts || [])
    .filter((part) => part.kind === 'file' && part.gcsPath && part.sandboxPath);
  return fileParts.find((part) => part.toolUseId === toolUseId) || null;
};

// Whether the working file behind an open canvas workbook has been changed
// somewhere else since the workbook was opened. `canvasFile` is the page's
// record of the open workbook: { gcsPath, sandboxPath, toolUseId }; a workbook
// opened from a still-streaming card has no paths yet, so they are taken from
// the agent's file part it was opened from.
//
// The file has changed elsewhere when a version later than the opened one
// exists and the newest version is not this page's own edit.
// `isOwnSend(clientMessageId)` says whether this page sent a message: its own
// latest edit is already in the open workbook, and replaced whatever came
// before it. When the opened version has not reached this page's view of the
// ledger yet, nothing known is later than it, so the answer is no: a version
// that merely differs from the opened one may be an older one.
export const workingFileChangedElsewhere = (messages = [], canvasFile, isOwnSend) => {
  if (!canvasFile) return false;
  let openedFromGcsPath = canvasFile.gcsPath || null;
  let sandboxPath = canvasFile.sandboxPath || null;
  if (!openedFromGcsPath || !sandboxPath) {
    const openedFromPart = agentFilePartForToolUse(messages, canvasFile.toolUseId);
    if (!openedFromPart) return false;
    openedFromGcsPath = openedFromGcsPath || openedFromPart.gcsPath;
    sandboxPath = sandboxPath || openedFromPart.sandboxPath;
  }
  const versions = workingFileVersions(messages, sandboxPath);
  const openedIndex = versions.findIndex((version) => version.gcsPath === openedFromGcsPath);
  if (openedIndex === -1) return false;
  const newestIndex = versions.length - 1;
  if (newestIndex === openedIndex) return false;
  return !isOwnSend(versions[newestIndex].clientMessageId);
};

// The §7.3 stream-death tie-break: compared by content (total text), never
// part count, so a half-streamed trailing block is never discarded.
export const totalTextLength = (parts = []) =>
  parts.reduce((n, p) => n + (typeof p.text === 'string' ? p.text.length : 0), 0);

// Auto-name for new chats (§7.2): first 48 characters of the message text
// (word-boundary truncated, "…"), else the first attachment's filename,
// else "New chat".
export const deriveAutoName = (text, files = []) => {
  const trimmed = (text || '').trim().replace(/\s+/g, ' ');
  if (trimmed) {
    if (trimmed.length <= 48) return trimmed;
    const slice = trimmed.slice(0, 48);
    const lastSpace = slice.lastIndexOf(' ');
    return `${(lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trim()}…`;
  }
  if (files.length > 0 && files[0]?.name) return files[0].name;
  return 'New chat';
};
